import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { MDXRemote } from "next-mdx-remote";

import { Layout } from "~landing/components/layout";
import { legalMdxComponents } from "~landing/components/legal/legal-mdx-components";
import type { LegalDoc } from "~landing/content/legal-types";

/**
 * Formats the front-matter `lastUpdated` ISO date as "15 May 2026". The locale
 * and time zone are pinned so the statically rendered markup and the hydrated
 * client agree.
 */
function formatLegalDate(iso: string): string {
	return new Intl.DateTimeFormat("en-GB", {
		day: "numeric",
		month: "long",
		year: "numeric",
		timeZone: "UTC",
	}).format(new Date(`${iso}T00:00:00Z`));
}

/**
 * The shell for every legal document — privacy, terms, security. The title and
 * date come from front-matter; everything below is MDX.
 *
 * Uses the plain `Layout`, not `MarketingPage`: legal pages want no hero, no
 * related-content strip and no trial CTA.
 */
export function LegalView({ frontmatter, mdxSource }: LegalDoc) {
	return (
		<Layout>
			<main>
				<Container maxWidth="md">
					<Stack
						spacing={2}
						sx={{ pt: 4 }}>
						<Typography
							gutterBottom
							variant="h1">
							{frontmatter.title}
						</Typography>
						<Typography variant="caption">
							Last updated on {formatLegalDate(frontmatter.lastUpdated)}
						</Typography>
					</Stack>

					{/*
					 * Not wrapped in the Stack above: the MDX component map carries its
					 * own vertical rhythm (`p` has my:2.5, `h2` has mt:6), so a Stack gap
					 * on top of it would double every gap.
					 */}
					<MDXRemote
						{...mdxSource}
						components={legalMdxComponents}
					/>
				</Container>
			</main>
		</Layout>
	);
}
